import React from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import YouTubeIcon from "@mui/icons-material/YouTube";
import BoltIcon from "@mui/icons-material/Bolt";
import RssFeedIcon from "@mui/icons-material/RssFeed";

import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../utils/formatEpisodeLengthToHumanFriendlyDuration";
import { formatFileSize } from "../../../utils/formatFileSize";
import { SourceMode } from "./Upload";
import { YouTubeVideoMetadata } from "../services";
import { RssEpisodeSelection } from "./RssImport";

interface SummaryProps {
  files: File[];
  fileDurationInSeconds: number;
  name: string;
  selectedProject: { label: string; id: string } | null;
  handleOpenPricingPlanDialog: () => void;
  isPlanLimitReached: boolean;
  sourceMode: SourceMode;
  youtubeMetadata: YouTubeVideoMetadata | null;
  rssEpisode: RssEpisodeSelection | null;
}

const sectionPaperSx: SxProps<Theme> = {
  p: 2.5,
  mb: 2,
  borderRadius: "12px",
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: (theme) =>
    theme.palette.mode === "light"
      ? theme.palette.grey[50]
      : theme.palette.grey[900],
};

const sectionTitleSx = {
  mb: 2,
  color: "#23272E",
  fontWeight: 600,
  fontSize: "1rem",
};

export default function Summary({
  files,
  fileDurationInSeconds,
  name,
  selectedProject,
  handleOpenPricingPlanDialog,
  isPlanLimitReached,
  sourceMode,
  youtubeMetadata,
  rssEpisode,
}: SummaryProps) {
  const file = files[0];
  const isAudio = file?.type.startsWith("audio");
  const isYouTube = sourceMode === "youtube";
  const isRss = sourceMode === "rss";
  let durationInSeconds = fileDurationInSeconds;
  if (isYouTube) durationInSeconds = youtubeMetadata?.duration_in_seconds ?? 0;
  else if (isRss)
    durationInSeconds = rssEpisode?.episode.duration_in_seconds ?? 0;

  let sourceLabel = "file";
  if (isYouTube) sourceLabel = "video";
  else if (isRss) sourceLabel = "episode";

  let sourceSectionTitle = "File";
  if (isYouTube) sourceSectionTitle = "YouTube video";
  else if (isRss) sourceSectionTitle = "RSS episode";

  return (
    <Box sx={{ width: "100%" }}>
      {isPlanLimitReached && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "12px",
              "& .MuiAlert-message": {
                fontSize: 14,
                fontWeight: 500,
              },
            }}
            action={
              <Button
                onClick={handleOpenPricingPlanDialog}
                variant="contained"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
                startIcon={<BoltIcon sx={{ color: "#ffd54f", fontSize: 18 }} />}
              >
                Upgrade
              </Button>
            }
          >
            You have reached the limit of maximum minutes allowed for your plan.
            Please upgrade your plan to continue.
          </Alert>
        </Box>
      )}

      <Typography
        variant="h5"
        sx={{ mb: 1, textAlign: "left", color: "#23272E", fontWeight: 700 }}
      >
        Review &amp; {isYouTube || isRss ? "Import" : "Upload"}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 3, color: "text.secondary", lineHeight: 1.5 }}
      >
        Confirm your {sourceLabel}, episode name, and project before{" "}
        {isYouTube || isRss ? "starting the import" : "starting the upload"}.
      </Typography>

      {/* Source section */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          {sourceSectionTitle}
        </Typography>

        {isRss && rssEpisode && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            {rssEpisode.episode.image_url ? (
              <Box
                component="img"
                src={rssEpisode.episode.image_url}
                alt="Episode artwork"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RssFeedIcon sx={{ color: "#2563eb", fontSize: 28 }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#23272E",
                  wordBreak: "break-word",
                }}
              >
                {rssEpisode.episode.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {rssEpisode.podcastTitle}
                {rssEpisode.episode.duration_in_seconds
                  ? ` · ${formatEpisodeLengthToHumanFriendlyDuration(rssEpisode.episode.duration_in_seconds)}`
                  : ""}
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Chip
                  icon={
                    <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                  }
                  label="Ready to import"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(22, 101, 52, 0.08)",
                    color: "#15803d",
                    fontWeight: 600,
                    borderRadius: "8px",
                    "& .MuiChip-icon": { color: "#15803d" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
        {isYouTube && youtubeMetadata && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            {youtubeMetadata.thumbnail_url ? (
              <Box
                component="img"
                src={youtubeMetadata.thumbnail_url}
                alt="YouTube thumbnail"
                sx={{
                  width: 96,
                  height: 54,
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 96,
                  height: 54,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <YouTubeIcon sx={{ color: "#dc2626", fontSize: 28 }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#23272E",
                  wordBreak: "break-word",
                }}
              >
                {youtubeMetadata.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                YouTube
                {" · "}
                {formatEpisodeLengthToHumanFriendlyDuration(
                  youtubeMetadata.duration_in_seconds,
                )}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                <Chip
                  icon={
                    <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                  }
                  label="Ready to import"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(22, 101, 52, 0.08)",
                    color: "#15803d",
                    fontWeight: 600,
                    borderRadius: "8px",
                    "& .MuiChip-icon": { color: "#15803d" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
        {!isRss && !isYouTube && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "10px",
                flexShrink: 0,
                backgroundColor: isAudio ? "#eff6ff" : "#fef2f2",
              }}
            >
              {isAudio ? (
                <AudioFileIcon sx={{ color: "#2563eb", fontSize: 24 }} />
              ) : (
                <VideoFileIcon sx={{ color: "#dc2626", fontSize: 24 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#23272E",
                  wordBreak: "break-word",
                }}
              >
                {file?.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {file?.type.split("/")[1].toUpperCase()}
                {" · "}
                {formatEpisodeLengthToHumanFriendlyDuration(
                  fileDurationInSeconds,
                )}
                {" · "}
                {formatFileSize(file?.size ?? 0, 1)}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                <Chip
                  icon={
                    <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                  }
                  label="Ready to upload"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(22, 101, 52, 0.08)",
                    color: "#15803d",
                    fontWeight: 600,
                    borderRadius: "8px",
                    "& .MuiChip-icon": { color: "#15803d" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Episode & project */}
      <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 0 }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Episode &amp; project
        </Typography>
        <Stack spacing={0}>
          <Box sx={{ py: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.75, fontSize: "0.8125rem" }}
            >
              Episode name
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {name}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ py: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.75, fontSize: "0.8125rem" }}
            >
              Project
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {selectedProject?.label ?? "—"}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ py: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.75, fontSize: "0.8125rem" }}
            >
              Duration
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {formatEpisodeLengthToHumanFriendlyDuration(durationInSeconds)}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
